import { get, set } from 'lodash-es';
import { ColDef } from 'ag-grid-community';
import { FieldPath } from 'react-hook-form';
import {
  CapitalAllocation,
  capitalAllocationFields,
  capitalAllocationSchema,
  cumulativeFundMetricsFields,
} from '../../../schemas/FundCapitalAllocation.schema';
import { schemaToFormFields } from '../../../util/schema-utils';
import { numericCellClasses } from '../../Finance2/common-grid-defs/commonColDefs';
import { IFormField } from '../../../view-models/form.view-model';
import { FMT } from '../../../util/formatter-service';
import { StringFormatterId } from '../../../util/formatters/StringFormatters';
import { getForesightStore } from '../../../util/jotai-store';
import { selectedFundStateFP } from '../state/FPState';
import { FundViewModel, fundViewModelSchema } from '../../../schemas/FundViewModel2.schema';
import { FundDataModel } from '../../../schemas/Fund.schema';
import {
  createFormatterDataModel,
  ICustomNumberFormatConfig,
} from '../../../data-models/formatter.data-model';

function fieldToGroup(field: CapitalAllocationRowData['field'] | FieldPath<CapitalAllocation>) {
  const fieldLc = field.toLowerCase();
  switch (true) {
    case fieldLc.includes('initial'):
      return 'Initial Investments';
    case fieldLc.includes('reserve'):
      return 'Reserves';
    case fieldLc.includes('fees'):
      return 'Fees & Expenses';
  }
}

export type FundAllocationField = Extract<
  FieldPath<FundViewModel>,
  | 'committedCapital'
  | 'feesAndExpensesAllocationAmount'
  | 'initialInvestmentAllocationAmount'
  | 'reservesAllocationAmount'
  | '_viewModel.totalAllocationAmount'
>;
export const FundFields = new Set<FundAllocationField>([
  'committedCapital',
  'feesAndExpensesAllocationAmount',
  'initialInvestmentAllocationAmount',
  'reservesAllocationAmount',
  '_viewModel.totalAllocationAmount',
]);

export interface CapitalAllocationRowData extends Record<string, unknown> {
  field: keyof CapitalAllocation | FundAllocationField;
  name: string;
  group?: string;
}

const excludedFieldsCapitalAllocation = new Set<FieldPath<CapitalAllocation>>([
  'cumulativeMetrics',
  'dateTo',
  'dateFrom',
  'period',
  'projectedFeesAndExpensesTransaction',
  'projectedFeesAndExpenses',
  'feesAndExpensesBalance',
  'projectedFeesAndExpensesBalance',
  'initialInvestmentsPacing',
  'initialInvestmentsPacingBalance',
  'reservesPacing',
  'reservesPacingBalance',
  'projectedReservesAmount',
  'allocatedCapitalBalance',
]);

const excludedFieldsCumulativeFundMetrics = new Set([
  'projectedFeesAndExpenses',
  'initialInvestmentsPacing',
  'initialInvestmentsPacingBalance',
  'reservesPacing',
  'reservesPacingBalance',
  'projectedReservesAmount',
  'allocatedCapitalBalance',
  'totalProjectedReservesAmount',
]);

const includedFields = () =>
  [
    ...(Object.keys(capitalAllocationFields()).filter(
      (path) => !excludedFieldsCapitalAllocation.has(path as FieldPath<CapitalAllocation>)
    ) as (keyof CapitalAllocation)[]),
    //    'projectedFeesAndExpensesTransaction.amount', MAGGIE-9861
    ...Object.keys(cumulativeFundMetricsFields())
      .filter(
        (key) =>
          !excludedFieldsCumulativeFundMetrics.has(
            key as keyof ReturnType<typeof cumulativeFundMetricsFields>
          )
      )
      .map((key) => `cumulativeMetrics.${key}`),
  ] as FieldPath<CapitalAllocation>[];

export function fundCapitalRows() {
  return getFundAllocationRows().totals;
}

export function getFundAllocationRows() {
  const fund = getForesightStore().get(selectedFundStateFP);
  const result = {
    totals: [] as CapitalAllocationRowData[],
    allocations: [] as CapitalAllocationRowData[],
  };
  if (!fund) {
    return result;
  }
  const fields = schemaToFormFields(fundViewModelSchema(), [...FundFields]);
  fields.forEach((field) => {
    const row =
      field.key === '_viewModel.totalAllocationAmount'
        ? getTotalAllocationRowData(fund)
        : {
            field: field.key as FundAllocationField,
            group: fieldToGroup(field.key as FieldPath<CapitalAllocation>),
            // some fields have the label set to "" for the form, so we need a fallback
            name: field.label || FMT.format(StringFormatterId.camelCaseToCapitalizedString, field.key),
            value: get(fund, field.key),
          };
    if (field.key === 'committedCapital' || field.key === '_viewModel.totalAllocationAmount') {
      result.totals.push(row);
    } else {
      result.allocations.push(row);
    }
  });

  return result;
}

function getTotalAllocationRowData(fund: FundDataModel) {
  return {
    field: '_viewModel.totalAllocationAmount',
    name: 'Total Allocation',
    value:
      ((get(fund, 'committedCapital') ?? 0) * (get(fund, 'allocatedCommittedCapitalPercentage') ?? 0)) / 100,
  } as CapitalAllocationRowData;
}

export function getCapitalAllocationRowData(data: CapitalAllocation[]): CapitalAllocationRowData[] {
  const result = [] as CapitalAllocationRowData[];
  result.push(...getFundAllocationRows().allocations);
  const fieldMap = schemaToFormFields(capitalAllocationSchema(), includedFields()).reduce(
    (map, field) => map.set(field.key, field),
    new Map<string, IFormField<unknown>>()
  );
  const dataByField = data.reduce(
    (acc, row) => {
      includedFields().forEach((field) => {
        const f = fieldMap.get(field);
        set(acc, [field, row.period], get(row, field));
        set(acc, [field, 'name'], f?.label ?? FMT.format(StringFormatterId.capitalizedString, field));
        set(acc, [field, 'field'], field);
        set(acc, [field, 'group'], fieldToGroup(field as FieldPath<CapitalAllocation>));
      });
      return acc;
    },
    {} as Record<keyof CapitalAllocation, CapitalAllocationRowData>
  );
  result.push(...Object.values(dataByField));
  return result;
}

export function getCapitalAllocationColDefs(
  data?: CapitalAllocation[] | null
): ColDef<CapitalAllocationRowData>[] {
  const fieldMap = schemaToFormFields(capitalAllocationSchema(), includedFields()).reduce(
    (map, field) => map.set(field.key, field),
    new Map<string, IFormField<unknown>>()
  );
  const usdShortFormatter = shortUsdWithDecimals();
  const periodDefs: ColDef<CapitalAllocationRowData>[] =
    data?.map((quarterlyData) => {
      return {
        field: quarterlyData.period,
        headerName: quarterlyData.period,
        suppressHeaderMenuButton: true,
        cellClass: numericCellClasses,
        valueFormatter: (params) => {
          if (params.value == null || !params.data) {
            return '';
          }
          return FMT.format(usdShortFormatter, params.value);
        },
        tooltipValueGetter: (params) => {
          if (params.value == null || !params.data) {
            return '';
          }
          const formatter = fieldMap.get(params.data.field)?.formatter;
          return formatter ? FMT.format(formatter, params.value) : params.value;
        },
      };
    }) ?? [];

  const nameDef: ColDef<CapitalAllocationRowData> = {
    field: 'name',
    headerName: '',
    pinned: true,
    filter: true,
    minWidth: NameColumnWidth,
    maxWidth: NameColumnWidth,
    tooltipField: 'name',
    resizable: false,
  };
  const groupDef: ColDef<CapitalAllocationRowData> = {
    hide: true,
    field: 'group',
    rowGroup: true,
    pinned: 'left',
  };

  return [groupDef, nameDef, ...periodDefs];
}

export const NameColumnWidth = 305;

export function shortUsdWithDecimals() {
  return createFormatterDataModel<ICustomNumberFormatConfig>({
    type: 'number',
    config: {
      compactDisplay: 'short',
      currency: 'USD',
      notation: 'compact',
      style: 'currency',
      maximumFractionDigits: 1,
      minimumFractionDigits: 1,
    },
  });
}
